import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import type { Currency } from '__generated__/globalTypes';
import { useMonetaryAmount } from 'hooks/useMonetaryAmount';

import { TransactionAmountWithConversion } from '../../common/TransactionAmountWithConversion';
import { TransactionEntry, messages } from '../../common/TransactionEntry';
import { AccountEntryState } from '../AccountEntryState';
import type { AccountEntry_userAccountEntry } from './__generated__/index.graphql';

type Props = {
  title: string | ReactNode;
  userAccountEntry: AccountEntry_userAccountEntry;
  children?: ReactNode;
  tooltip?: ReactNode;
  icon?: ReactNode;
  expandable?: boolean;
  primaryCurrency?: Currency;
};

export const PriceContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: baseline;
  text-align: right;
`;

export { messages };

export const AccountEntry = ({
  title,
  userAccountEntry,
  children,
  tooltip,
  icon,
  expandable,
  primaryCurrency,
}: Props) => {
  const { amounts } = userAccountEntry;
  const { toMonetaryAmount } = useMonetaryAmount();
  const monetaryOutput = toMonetaryAmount(amounts);
  const monetaryOutputIsPositive = monetaryOutput.isPositive;

  return (
    <TransactionEntry
      date={userAccountEntry.date}
      expandable={expandable}
      amount={
        <PriceContainer>
          {tooltip}
          <TransactionAmountWithConversion
            monetaryAmount={amounts}
            primaryCurrency={primaryCurrency}
            aasmState={userAccountEntry.aasmState}
            positive={monetaryOutputIsPositive}
          />
        </PriceContainer>
      }
      state={<AccountEntryState userAccountEntry={userAccountEntry} />}
      title={title}
      icon={icon}
    >
      {children}
    </TransactionEntry>
  );
};

AccountEntry.fragments = {
  userAccountEntry: gql`
    fragment AccountEntry_userAccountEntry on UserAccountEntry {
      id
      aasmState
      amounts {
        ...useMonetaryAmount_monetaryAmount
      }
      date
      ...AccountEntryState_userAccountEntry
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
    ${AccountEntryState.fragments.userAccountEntry}
  ` as TypedDocumentNode<AccountEntry_userAccountEntry>,
};
